import { Map as ImmutableMap } from "immutable"
import { FetchState, getValue, hasValue } from "./FetchState"
import { Result } from "../Utility/Result"

type GetId<M, K> = (model: M) => K

export interface Page<K> {
    items: K[]
    isStale: boolean
}

/**
 * Represents a collection of remote resource.
 *
 * There are three type parameters:
 * - `M` is the type of model
 * - `P` is the type of parameters to fetch a list of the resoure
 * - `K` is the type of id to identify model.
 */
export interface RemoteData<M, P, K> {
    list:
        | {
              // Parameters used when fetching the list
              params: P
              // List of fetched pages of the resource.
              pages: Page<K>[]
              // Total count of items
              total: number
              // additional info about the page if any
              meta?: object
          }
        | undefined
    records: ImmutableMap<K, FetchState<M>>
}

export function getPrevItemId<M, P, K>(
    data: RemoteData<M, P, K>,
    id: K
): K | undefined {
    if (data.list === undefined) {
        return undefined
    }

    return getIdList(data).find((_, index, items) => items[index + 1] === id)
}

export function getNextItemId<M, P, K>(
    data: RemoteData<M, P, K>,
    id: K
): K | undefined {
    if (data.list === undefined) {
        return undefined
    }

    return getIdList(data).find((_, index, items) => items[index - 1] === id)
}

export function isLastFetchedItem<M, P, K>(
    data: RemoteData<M, P, K>,
    id: K
): boolean {
    const ids = getIdList(data)

    return ids.length > 0 && ids[ids.length - 1] === id
}

export function hasMore<M, P, K>(data: RemoteData<M, P, K>): boolean {
    return data.list === undefined || data.list.total > getIdList(data).length
}

/**
 * Find and fix inconsistency in pages
 *
 * Specifically, this function makes sure that
 * - number of items in all pages doesn't exceed total and
 * - there's no duplication through pages.
 *
 * This matters when data is changing dynamically
 * (e.g. job list is filtered by status=IN_PROGRESS and they get processed over time)
 *
 * To be fully consistent, we need to discard all pages and
 * start fetching from first page, but I'm not doing so because
 * that behavior would be annoying when user is scrolling through table.
 */
function sanitizePages<K>(pages: Page<K>[], total: number): Page<K>[] {
    const sanitizedPages: Page<K>[] = []
    let count = 0
    let appearedIds = new Map<K, true>()

    for (let index = 0; index < pages.length; index++) {
        const page = pages[index]
        const dedupedItems = page.items.filter(id => !appearedIds.has(id))

        count = count + dedupedItems.length
        dedupedItems.forEach(id => appearedIds.set(id, true))
        sanitizedPages.push({
            ...page,
            items:
                count > total
                    ? dedupedItems.slice(0, total - count)
                    : dedupedItems
        })
    }

    return sanitizedPages
}

/**
 * Returns `RemoteData` whose list is updated with given items.
 * If given `remoteData` has one or more page and params are not changed, a new page is appended to the list.
 * Otherwise, list is initialized with given items.
 */
export function setPage<M, P, K>(
    data: RemoteData<M, P, K>,
    params: P,
    items: M[],
    getId: GetId<M, K>,
    page: number,
    total: number,
    meta?: object
): RemoteData<M, P, K> {
    const newPage = {
        items: items.map(getId),
        isStale: false
    }
    const pages =
        data.list === undefined ||
        JSON.stringify(data.list.params) !== JSON.stringify(params)
            ? [newPage]
            : page < data.list.pages.length
            ? data.list.pages.map((p, i) => (i === page ? newPage : p))
            : data.list.pages.concat(newPage)

    return {
        list: { params, total, pages: sanitizePages(pages, total), meta },
        records: mergeRecords(data.records, items, getId)
    }
}

export function setList<M, P, K>(
    data: RemoteData<M, P, K>,
    params: P,
    items: M[],
    getId: GetId<M, K>
): RemoteData<M, P, K> {
    return setPage(data, params, items, getId, 0, items.length)
}

/**
 * Returns `RemoteData` which is updated with given item ID.
 * If the item is in the list, pages that can be affected by the update are marked as stale for refetching.
 */
export function setItem<M, P, K>(
    data: RemoteData<M, P, K>,
    itemID: K,
    item: M | undefined,
    getId: GetId<M, K>
): RemoteData<M, P, K> {
    const updated = {
        ...data,
        records: mergeRecords(
            data.records,
            item === undefined ? [] : [item],
            getId
        )
    }
    const page = data.list?.pages.findIndex(p => p.items.includes(itemID)) ?? -1

    return page >= 0 ? setPagesToStale(updated, page) : updated
}

/**
 * Returns `RemoteData` whose pages are marked as stale.
 */
export function setPagesToStale<M, P, K>(
    data: RemoteData<M, P, K>,
    pageFrom: number
): RemoteData<M, P, K> {
    const updatePage = (page: Page<K>, index: number): Page<K> =>
        index >= pageFrom ? { ...page, isStale: true } : page

    return {
        ...data,
        list: data.list && {
            ...data.list,
            pages: data.list.pages.map(updatePage)
        }
    }
}

/**
 * Returns first index of the page that needs to be (re)fetched
 */
export function getPageToFetch<M, P, K>(
    data: RemoteData<M, P, K> | undefined,
    params: P,
    refetchStalePage: boolean = true
): number {
    if (
        data?.list === undefined ||
        JSON.stringify(data.list.params) !== JSON.stringify(params)
    ) {
        return 0
    }

    return refetchStalePage
        ? data.list.pages.findIndex(page => page.isStale)
        : -1
}

/**
 * Returns value for specified id if the argument is `Ok`.
 */
export function getValueById<M, P, K>(
    data: RemoteData<M, P, K> | undefined,
    id: K
): M | undefined {
    return data === undefined ? undefined : getValue(data.records.get(id))
}

/**
 * Returns value which satisfies the given predicate if the argument is `Ok`.
 */
export function findValue<M, P, K>(
    data: RemoteData<M, P, K> | undefined,
    predicate: (value: M) => boolean
): M | undefined {
    return data === undefined
        ? undefined
        : getValue(data.records.find(r => hasValue(r) && predicate(r.value)))
}

export function mergeRecords<M, K>(
    currentRecords: ImmutableMap<K, FetchState<M>>,
    newItems: M[],
    getId: GetId<M, K>
): ImmutableMap<K, FetchState<M>> {
    return newItems.reduce(
        (passed, item) => passed.set(getId(item), Result.ok(item)),
        currentRecords
    )
}

export function mergeErrors<M, K>(
    currentRecords: ImmutableMap<K, FetchState<M>>,
    errors: ImmutableMap<K, string>
): ImmutableMap<K, FetchState<M>> {
    return errors.reduce(
        (passed, message, id) =>
            passed === undefined
                ? ImmutableMap<K, FetchState<M>>()
                : id === undefined || message === undefined
                ? passed
                : passed.set(id, Result.error(message)),
        currentRecords
    )
}

export function getIdList<M, P, K>(remoteData: RemoteData<M, P, K>): K[] {
    if (remoteData.list === undefined) return []

    return remoteData.list.pages.flatMap(page => page.items)
}

export function getItemList<M, P, K>(remoteData: RemoteData<M, P, K>): M[] {
    return getIdList(remoteData).flatMap(id => {
        const result = getValueById(remoteData, id)

        return result === undefined ? [] : [result]
    })
}

export function getEmptyStalePageOrStalePagesContaining<M, P, K>(
    remoteData: RemoteData<M, P, K> | undefined,
    ids: K[]
): number[] {
    return remoteData?.list?.pages.some(
        page => page.items.length === 0 && page.isStale
    ) ?? false
        ? [0]
        : remoteData?.list?.pages.flatMap((p, i) =>
              p.isStale && ids.some(id => p.items.includes(id)) ? [i] : []
          ) ?? []
}

export function getInitialData<M, P, K>(): RemoteData<M, P, K> {
    return {
        list: undefined,
        records: ImmutableMap<K, FetchState<M>>()
    }
}

export function markAllPagesOfAllEntriesAsStale<K extends string, M, P, KK>(
    entries: { [key in K]: RemoteData<M, P, KK> | undefined }
): { [key in K]: RemoteData<M, P, KK> | undefined } {
    return Object.entries<RemoteData<M, P, KK> | undefined>(entries).reduce(
        (accum, [key, remoteData]) => {
            accum[key as K] = setPagesToStale(remoteData ?? getInitialData(), 0)
            return accum
        },
        {} as { [key in K]: RemoteData<M, P, KK> | undefined }
    )
}
